import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";
import { cn } from "@/lib/utils/cn";

type Toast = {
  id: string;
  kind: "success" | "error" | "info";
  message: string;
  ttl?: number;
};

type ToasterContextValue = {
  push: (t: Omit<Toast, "id">) => void;
};

const ToasterContext = createContext<ToasterContextValue | null>(null);

export function ToasterProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((t: Omit<Toast, "id">) => {
    const id = Math.random().toString(36).slice(2);
    const toast: Toast = { ...t, id };
    setToasts((curr) => [...curr, toast]);
    setTimeout(() => {
      setToasts((curr) => curr.filter((x) => x.id !== id));
    }, t.ttl ?? 4500);
  }, []);

  // Auto-pickup de errores 403 del axios interceptor.
  useEffect(() => {
    function handle(e: Event) {
      const detail = (e as CustomEvent).detail as { message: string; missingPermission?: string };
      push({
        kind: "error",
        message: detail.missingPermission
          ? `${detail.message} (falta: ${detail.missingPermission})`
          : detail.message,
      });
    }
    window.addEventListener("nx:auth:forbidden", handle);
    return () => window.removeEventListener("nx:auth:forbidden", handle);
  }, [push]);

  return (
    <ToasterContext.Provider value={{ push }}>
      {children}
      <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 max-w-sm">
        {toasts.map((t) => (
          <ToastItem
            key={t.id}
            toast={t}
            onDismiss={() => setToasts((curr) => curr.filter((x) => x.id !== t.id))}
          />
        ))}
      </div>
    </ToasterContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToasterContext);
  if (!ctx) throw new Error("useToast must be used within ToasterProvider");
  return ctx;
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const Icon = toast.kind === "success" ? CheckCircle2 : toast.kind === "error" ? AlertCircle : Info;
  const colorClass =
    toast.kind === "success"
      ? "border-success/30 text-success"
      : toast.kind === "error"
      ? "border-danger/30 text-danger"
      : "border-info/30 text-info";

  return (
    <div
      className={cn(
        "flex items-start gap-2 p-3 rounded-lg shadow-nx-lg border bg-bg-elevated animate-fade-in",
        colorClass,
      )}
      role="status"
    >
      <Icon size={16} className="shrink-0 mt-0.5" />
      <p className="flex-1 text-sm text-fg">{toast.message}</p>
      <button
        onClick={onDismiss}
        className="text-fg-subtle hover:text-fg transition-colors -mr-1 p-0.5"
        aria-label="Cerrar"
      >
        <X size={14} />
      </button>
    </div>
  );
}
